import { debounce } from "lodash-es";
import type { RefObject } from "react";
import { useDispatch } from "react-redux";
import { useHotKeys } from "widgets/hooks";
import type { StoreApi, UseBoundStore } from "zustand";

import { pasteFiles, storeFilesToBeCopied, storeFilesToBeCut } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { Hotkeys } from "@/settings/hotkeys.ts";
import type InMemoryTableWithSelectionModel from "@/ui-modules/core/grid/InMemoryTableWithSelectionModel.js";

import type { FileBrowserStore } from "../stores/file-browser.ts";

export const useFileBrowserHotKeys = (
  hotKeysRef: RefObject<HTMLElement>,
  useStore: UseBoundStore<StoreApi<FileBrowserStore>>,
  tableRef: RefObject<InMemoryTableWithSelectionModel>,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const refresh = useStore((state) => state.refresh);
  const currentFolder = useStore((state) => state.currentFolder);
  const selectedFiles = useStore((state) => state.selectedFiles);

  useHotKeys(
    Hotkeys.files.copy,
    () => selectedFiles.length > 0 && dispatch(storeFilesToBeCopied(selectedFiles)),
    hotKeysRef,
    // support the Ctrl-c hotkey only if at least one file is selected,
    // which enables copying of cell contents in file browser without selection
    { disabled: selectedFiles.length === 0 },
  );
  useHotKeys(
    Hotkeys.files.cut,
    () => selectedFiles.length > 0 && dispatch(storeFilesToBeCut(selectedFiles)),
    hotKeysRef,
  );
  useHotKeys(
    Hotkeys.files.paste,
    // Debounced to avoid keydown repetition if you keep the key pressed
    debounce(
      () =>
        dispatch(pasteFiles(currentFolder))
          .then((params) => {
            // refresh the current folder if it corresponds to the source or target folders
            if (params.targetFolderId === currentFolder.id || params.sourceFolderId === currentFolder.id) {
              refresh();
            }
          })
          .then(() => {
            tableRef.current?.resetSelection();
          }),
      2000,
      { leading: true },
    ),
    hotKeysRef,
  );
};
